import React from 'react';
import { FaMinus, FaPlus } from 'react-icons/fa';
import cx from '@utils/cx.util';
import { BiMinus, BiPlus } from 'react-icons/bi';

type Props = {
  increaseHandler?: () => void;
  decreaseHandler?: () => void;
  large?: boolean;
  inputId?: string;
  inputRegister?: any;
  minQuantity?: number;
  maxQuantity?: number;
  value: number;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  disabled?: boolean;
  isReadOnly?: boolean;
  [key: string]: any;
};

const QuantityInput = React.forwardRef<HTMLInputElement, Props>(
  (
    {
      increaseHandler,
      decreaseHandler,
      large = false,
      inputId = 'quantity',
      inputRegister = null,
      minQuantity = 1,
      maxQuantity,
      value,
      onChange,
      disabled = false,
      isReadOnly = false,
      className = '',
      isPDPVariant = false,
      ...otherProps
    }: Props,
    ref
  ) => {
    const decreaseHandlerDisabled = !!minQuantity && minQuantity === value;
    const increaseHandlerDisabled = !!maxQuantity && maxQuantity === value;

    return (
      <div
        className={cx(
          'tw:inline-flex tw:items-center tw:gap-1 tw:rounded-button tw:border tw:border-brand-black tw:bg-white tw:px-3 tw:text-body tw:lg:px-0',
          {
            'tw:rounded-full tw:px-0': isPDPVariant,
          },
          className
        )}>
        {!isPDPVariant && (
          <label
            className='tw:inline-block tw:text-sm tw:font-bold tw:lg:hidden tw:lg:text-base'
            htmlFor={inputId}>
            QTY
          </label>
        )}
        <BaseIcon
          isPDPVariant={isPDPVariant}
          role='button'
          disabled={disabled || decreaseHandlerDisabled}
          large={large}
          onClick={decreaseHandler}
          aria-label='Decrease Quantity'>
          {!isReadOnly && (
            <>
              {isPDPVariant ? (
                <BiMinus
                  size={16}
                  className={cx('tw:transition tw:duration-200', {
                    'tw:text-body': !decreaseHandlerDisabled,
                    'tw:text-gray-300': decreaseHandlerDisabled,
                  })}
                />
              ) : (
                <FaMinus
                  size={16}
                  className={cx('tw:transition tw:duration-200', {
                    'tw:text-body': !decreaseHandlerDisabled,
                    'tw:text-gray-300': decreaseHandlerDisabled,
                  })}
                />
              )}
            </>
          )}
        </BaseIcon>
        <QuantityInputElement
          ref={ref}
          type='text'
          id={inputId}
          inputMode='number'
          className={cx(
            'tw:max-w-[30px] tw:appearance-none tw:text-center tw:font-bold tw:focus:outline-hidden',
            {
              'tw:h-[40px] tw:text-sm tw:lg:text-base': !large || isPDPVariant,
              'tw:h-[40px] tw:text-sm tw:lg:h-[48px] tw:lg:text-lg': large && !isPDPVariant,
              'tw:h-9 tw:max-w-[32px] tw:font-medium': isPDPVariant,
            },
            {
              'tw:text-body': !disabled,
              'tw:text-gray-300': disabled,
            }
          )}
          min={minQuantity}
          max={maxQuantity ?? null}
          readOnly={isReadOnly}
          register={inputRegister}
          onChange={onChange}
          value={value}
          {...otherProps}
        />
        <BaseIcon
          isPDPVariant={isPDPVariant}
          role='button'
          disabled={disabled || increaseHandlerDisabled}
          large={large}
          onClick={increaseHandler}
          aria-label='Increase Quantity'>
          {!isReadOnly && (
            <>
              {isPDPVariant ? (
                <BiPlus
                  size={16}
                  className={cx('tw:transition tw:duration-200', {
                    'tw:text-body': !increaseHandlerDisabled,
                    'tw:text-gray-300': increaseHandlerDisabled,
                  })}
                />
              ) : (
                <FaPlus
                  size={16}
                  className={cx('tw:transition tw:duration-200', {
                    'tw:text-body': !increaseHandlerDisabled,
                    'tw:text-gray-300': increaseHandlerDisabled,
                  })}
                />
              )}
            </>
          )}
        </BaseIcon>
      </div>
    );
  }
);

const QuantityInputElement = React.forwardRef<HTMLInputElement, Props>(
  ({ register, onChange, value, ...restProps }: Props, ref) => {
    if (!!register) {
      return <input ref={ref} {...register} {...restProps} />;
    }

    const onChangeOverride = (e: React.ChangeEvent<HTMLInputElement>) => {
      const { value } = e.target;

      // only allow numbers or empty text
      if (value === '' || !isNaN(Number(value))) {
        onChange(e);
      }
    };

    if (!!onChange)
      return (
        <input
          ref={ref}
          className='tw:rounder-r-full tw:lg:rounded-r-none'
          onChange={onChangeOverride}
          value={value}
          {...restProps}
        />
      );

    return null;
  }
);

const BaseIcon = ({
  className = '',
  isPDPVariant = false,
  large = false,
  children,
  disabled,
  ...restProps
}) => {
  return (
    <span
      className={cx(
        'tw:hidden tw:cursor-pointer tw:items-center tw:justify-center tw:transition tw:duration-200 tw:lg:inline-flex',
        {
          'tw:h-[30px] tw:w-[30px]': !large && !isPDPVariant,
          'tw:h-[30px] tw:w-[30px] tw:xl:h-[50px]': large && !isPDPVariant,
          'tw:inline-flex tw:h-6 tw:w-6': isPDPVariant,
        },
        className
      )}
      {...restProps}>
      {children}
    </span>
  );
};

export default QuantityInput;
